import { Alert, Anchor, Button, Code, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { IconAlertCircle, IconCircleCheck, IconInfoCircle, IconMail } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { loginSchema, type LoginInput } from '@creativitas/shared';
import { toUserMessage } from '@/lib/api';
import { pendingReturnTo } from '@/lib/returnTo';
import { fetchCapabilities, resendVerification } from './api';
import { AuthLayout } from './AuthLayout';
import { useLogin } from './useSession';

/** Demo accounts are printed on the login screen so the owner can try each role. */
const DEMO_ACCOUNTS = [
  ['admin@creativitas.local', 'администратор'],
  ['owner@creativitas.local', 'владелец игры'],
  ['trial@creativitas.local', 'пробный период'],
  ['subscriber@creativitas.local', 'подписка'],
] as const;

/**
 * The demo hint is printed for whoever opens the prototype locally.
 *
 * The moment the dev server is shown through a tunnel the same page would hand
 * a stranger the administrator's password, so it is bound to the machine that
 * runs it: on any other host the block is simply absent.
 */
const onOwnMachine =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1', '[::1]', '::1'].includes(window.location.hostname);

export function LoginPage() {
  /*
    «Забыли пароль?» показывается, только если на этой установке настроена
    почта. Предлагать письмо, которого не будет, хуже, чем не предлагать
    восстановление совсем: человек уйдёт ждать и не станет писать в поддержку.
  */
  const capabilities = useQuery({
    queryKey: ['auth', 'capabilities'],
    queryFn: fetchCapabilities,
    staleTime: Infinity,
  });

  const loginMutation = useLogin();
  // Пришли по платёжной ссылке без входа: объясняем, что будет дальше.
  const purchase = pendingReturnTo() !== null;
  const location = useLocation();
  const registration = location.state as {
    /* Сюда же приходят после удаления аккаунта (docs/PRIVACY.md). */
    accountDeleted?: boolean;
    registered?: boolean;
    email?: string;
    awaitingEmailVerification?: boolean;
    verificationMailSent?: boolean;
  } | null;

  /*
    Повторное письмо предлагается там, где о нём вспоминают: сразу после
    регистрации и после отказа «почта не подтверждена». В остальное время
    ссылка на форму никому не нужна и только загромождает вход.
  */
  const resend = useMutation({ mutationFn: (email: string) => resendVerification({ email }) });
  const unverified =
    loginMutation.isError && /не подтверждена/i.test(toUserMessage(loginMutation.error));

  const form = useForm<LoginInput>({
    initialValues: { email: registration?.email ?? '', password: '' },
    validate: zodResolver(loginSchema),
    validateInputOnBlur: true,
  });

  return (
    <AuthLayout title="Вход" subtitle="Войдите, чтобы открыть МАК-сессии, игры и обучение.">
      <form onSubmit={form.onSubmit((values) => loginMutation.mutate(values))} noValidate>
        <Stack gap="md">
          {purchase && !registration?.registered ? (
            <Alert color="blue" icon={<IconInfoCircle size={17} />} data-testid="login-purchase-hint">
              Войдите — и откроется оплата. Нет аккаунта? Зарегистрируйтесь, подтвердите почту по ссылке из письма и
              войдите: оплата откроется после входа.
            </Alert>
          ) : null}
          {registration?.accountDeleted ? (
            <Alert color="teal" icon={<IconCircleCheck size={17} />} title="Аккаунт удалён">
              Ваши данные стёрты с платформы. Спасибо, что были с нами.
            </Alert>
          ) : null}
          {registration?.registered ? (
            registration.awaitingEmailVerification ? (
              <Alert color="teal" icon={<IconMail size={17} />} title="Проверьте почту">
                {registration.verificationMailSent
                  ? `Мы отправили письмо на ${registration.email}. Откройте ссылку из него — и входите.`
                  : 'Аккаунт создан, но письмо отправить не удалось. Попробуйте запросить его заново.'}
              </Alert>
            ) : (
              <Alert color="teal" icon={<IconCircleCheck size={17} />} title="Аккаунт создан">
                Войдите в аккаунт — с этого момента начнётся пробный период.
              </Alert>
            )
          ) : null}
          {loginMutation.isError ? (
            <Alert color="red" icon={<IconAlertCircle size={17} />} title="Не удалось войти">
              {toUserMessage(loginMutation.error)}
            </Alert>
          ) : null}

          {(unverified || (registration?.registered && registration.awaitingEmailVerification)) &&
          capabilities.data?.emailVerification ? (
            resend.isSuccess ? (
              <Alert color="teal" icon={<IconMail size={17} />} title="Письмо отправлено">
                Если такой адрес зарегистрирован, письмо уже в пути.
              </Alert>
            ) : (
              <Button
                variant="light"
                leftSection={<IconMail size={16} />}
                loading={resend.isPending}
                disabled={!form.values.email.includes('@')}
                onClick={() => resend.mutate(form.values.email.trim())}
              >
                Отправить письмо о подтверждении заново
              </Button>
            )
          ) : null}

          <TextInput
            label="Email"
            placeholder="you@example.com"
            type="email"
            autoComplete="email"
            required
            {...form.getInputProps('email')}
          />

          <PasswordInput
            label="Пароль"
            placeholder="Ваш пароль"
            autoComplete="current-password"
            required
            {...form.getInputProps('password')}
          />

          <Button type="submit" fullWidth loading={loginMutation.isPending}>
            Войти
          </Button>

          {onOwnMachine ? (
          <Alert variant="light" color="teal" icon={<IconInfoCircle size={16} />} p="sm">
            <Text size="xs" fw={600} mb={4}>
              Демо-аккаунты, пароль <Code>creativitas</Code>
            </Text>
            <Stack gap={2}>
              {DEMO_ACCOUNTS.map(([email, role]) => (
                <Text key={email} size="xs" c="var(--text-muted)">
                  <Anchor
                    component="button"
                    type="button"
                    size="xs"
                    onClick={() => form.setValues({ email, password: 'creativitas' })}
                  >
                    {email}
                  </Anchor>{' '}
                  — {role}
                </Text>
              ))}
            </Stack>
          </Alert>
          ) : null}
        </Stack>
      </form>

      {capabilities.data?.passwordReset ? (
        <Text size="sm" c="var(--text-muted)" ta="center" mt="md">
          <Anchor component={Link} to="/forgot-password">
            Забыли пароль?
          </Anchor>
        </Text>
      ) : null}

      <Text size="sm" c="var(--text-muted)" ta="center" mt="md">
        Нет аккаунта?{' '}
        <Anchor component={Link} to="/register">
          Зарегистрироваться
        </Anchor>
      </Text>
    </AuthLayout>
  );
}
